import { DatePipe } from "@angular/common";
import { Component, ElementRef, OnDestroy, OnInit } from "@angular/core";
import { BsModalService } from "ngx-bootstrap/modal";
import { AppState, ShowRequest } from "../../core";

import { RutCallLogRecord, RutCallLogViewRecord } from "../../core/models/rut-call-log-record";
import {
  DriveTestSeqService,
  PathTestService,
  PostProcessingService,
} from "../../core/services";
import { TestSeqService } from "../../core/services/test-seq.service";
import {
  StatMetric,
  TableViewComponentBase,
  TableViewQueryRequest,
} from "../../shared/components/table-view/table-view-component-base";
import { VolunteerTaskPointService } from "../../volunteer/services/volunteer-task-point.service";
import { VolunteerEditComponent } from "../../volunteer/volunteer-edit/volunteer-edit.component";
import { VolunteerService } from "../../volunteer/services/volunteer.service";
import { firstValueFrom } from "rxjs";
import * as moment from 'moment';
import Moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
momentDurationFormatSetup(moment);

@Component({
  selector: "app-rut-call-log-table",
  templateUrl: "../../shared/components/table-view/table-view.component.html",
  styleUrls: [
    "../../shared/components/table-view/scss/table-view.scss",
    "../../shared/components/table-view/scss/app-tab-full-height.scss",
    "../../shared/components/table-view/scss/fixed-headers-and-column.scss"
  ],
})
export class RutCallLogTableComponent
  extends TableViewComponentBase<RutCallLogViewRecord>
  implements OnInit, OnDestroy
{
  constructor(
    public drivetestSeqService: DriveTestSeqService,
    public testSeqService: TestSeqService,
    public pathTestService: PathTestService,
    public volTaskPointService: VolunteerTaskPointService,
    public bsModalService: BsModalService,
    public postProcessingService: PostProcessingService,
    public volunteerService: VolunteerService,
    appState: AppState,
    elementRef: ElementRef,
    datePipe: DatePipe
  ) {
    super(appState, bsModalService, elementRef, datePipe);
    this.tableColumns = [
      { title: "ID", name: "id", visible: true },
      { title: "User ID", name: "userId", visible: false },
      { title: "User Name", name: "userName", visible: true },
      { title: "User Email", name: "userEmail", visible: false },
      { title: "Point ID", name: "pointId", visible: true },
      { title: "Task ID", name: "taskId", visible: true, filter: { key: 'taskId', value: '', mode: '='} },
      { title: "Task Point ID", name: "taskPointId", visible: true, filter: { key: 'taskPointId', value: '', mode: '='} },
      { title: "Device ID", name: "deviceId", visible: true },
      { title: "Phone Number", name: "phoneNum", visible: false },
      { title: "Phone Account ID", name: "phoneAccountId", visible: false },
      { title: "Dialed Number", name: "dialedNum", visible: true },
      {
        title: "Start Time (UTC)",
        name: "startTime",
        visible: false,
      },
      {
        title: "Start Time (local)",
        name: "localStartTime",
        visible: true,
      },
      {
        title: "End Time (UTC)",
        name: "endTime",
        visible: false,
        template: "fulltime",
      },
      {
        title: "End Time (local)",
        name: "localEndTime",
        visible: false,
        template: "localtime",
      },
      {
        title: "QR Scan Time (UTC)",
        name: "qrScanTime",
        visible: false,
        template: "fulltime",
      },
      {
        title: "QR Scan Time (local)",
        name: "qrScanTime",
        visible: false,
        template: "local",
      },
      {
        title: "Call Duration",
        name: "duration",
        visible: true,
        template: "correlationLink",
      },
      { title: "Call Setup", name: "setup", visible: true },
      { title: "Call Traffic", name: "traffic", visible: true },
      { title: "Carrier (OneSource)", name: "oneSourceCarrier", visible: false, template: 'warn-white-space' },
      { title: "Carrier (Network)", name: "carrier", visible: true, template: 'warn-white-space' },
      { title: "Disconnect Reason", name: "disconnectReason", visible: false },
      { title: "Updated At", name: "updatedAt", visible: false },
      { title: "QR Scan Age (secs)", name: 'qrScanAgeSecs', visible: true },
      { title: "Valid", name: "validTestCall", template: 'boolean', visible: true },
      { title: "Photos", name: 'photoCnt', template: 'photos', visible: true },
      { title: "Validation Latitude", name: 'validationLat', template: 'number', visible: false },
      { title: "Validation Longitude", name: 'validationLng', template: 'number', visible: false },
      { title: "Comments", name: "comments", visible: true },
      { title: "Remote Disconnect (Survey)", name: "remoteDisconnect", template: 'badboolean', visible: false },
      { title: "Hit PSAP (Survey)", name: "hitPsap", template: 'badboolean', visible: true },
      //{ title: "Duration OK (Survey)", name: "durationOk", template: 'boolean', visible: false },
    ];
    this.sortColumns = [
      { key: 'localStartTime', order: 'DESC'},
      { key: 'localEndTime', order: 'DESC'},
    ];
    this.getName = () => 'RUT Call';
    this.virtualColNames = {
      localStartTime: 'startTime',
      localEndTime: 'endTime'
    }
    this.pageSize = 100;
    const metrics: StatMetric[] = [
      { column: 'duration', statTypes: ['min','max','avg']},
      { column: 'setup', statTypes: ['min','max','avg']},
      { column: 'traffic', statTypes: ['min','max','avg']},
      { column: 'startTime', format: { dataType: 'date' }, statTypes: ['min','max']},
      { column: '*', format: { dataType: 'number' }, statTypes: ['count']},
    ];
    this.statGroups = [
      {
        id: 'byCarrier',
        groupBy: ['carrier'],
        metrics,
        visible: true,
      },
      {
        id: 'userEmail',
        groupBy: ['userEmail'],
        metrics,
      },
      {
        id: 'dialedNum',
        groupBy: ['dialedNum'],
        metrics,
      },
      {
        id: 'hitPsap',
        groupBy: ['hitPsap'],
        metrics,
      },
    ];
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  doQuery = async (queryReq: TableViewQueryRequest) => {
    const results = await this.postProcessingService.findRutCallLogRecords(queryReq);
    if ( queryReq.sorts?.[0]?.key === 'startTime') {
      const rows = results.rows;
      for( let i=1; i < rows?.length; i++ ) {
        try {
          const row = rows[i];
          const duration = Moment.duration(Moment(rows[i-1].startTime).diff(Moment(row.startTime)));
          row['callGapSecs'] = (duration as any).format('D [days] h:mm:ss', {trim: 'left'})
        } catch ( e: any ) {
          console.warn('Failed computing call gap seconds for rows',i-1, i);
        }
      }
    }
    return results;
  }

  showPointId(record: RutCallLogRecord) {
    this.showRequestFn(new ShowRequest("tab", "point", record.pointId, null));
  }

  async showTask(record: RutCallLogViewRecord) {
    try {
      const taskIdObj = {
        taskId: record.taskId,
      }
      this.showRequestFn(new ShowRequest("tab", "volunteer task", JSON.stringify(taskIdObj), null));
    } catch ( e: any ) {
      this.volTaskPointService.publishError(e,this,60);
    }
  }

  async showTaskPoint(record: RutCallLogViewRecord) {
    try {
      const volTaskPoint = await this.volTaskPointService.getById(record.taskPointId);
      if ( !volTaskPoint ) {
        this.appState.notifyWarning('Could not find task associated with task point',this,5);
        return;
      }
      const taskIdObj = {
        taskId: volTaskPoint.taskId,
        taskPointId: record.taskPointId
      }
      this.showRequestFn(new ShowRequest("tab", "volunteer task", JSON.stringify(taskIdObj), null));
    } catch ( e: any ) {
      this.volTaskPointService.publishError(e,this,60);
    }
  }

  async showMobileUser(record: RutCallLogViewRecord) {
    const volunteer = await this.volunteerService.getByIdEagerly(record.userId);
    if ( !volunteer ) {
      this.appState.notifyWarning(`No user found for ID '${record.userId}' (${record.userName})`, this, 5);
    }
    const modal = VolunteerEditComponent.showModal(this.bsModalService,volunteer);
    await firstValueFrom(modal.onHidden);
    if ( !modal.content?.cancelled ) {
      this.load().catch(e=>this.volunteerService.publishError);
    }
  }

}
